import { Slider } from '@miblanchard/react-native-slider'
import { Color } from 'zens-storybook'
import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { StyleSheet, View, ViewProps, ViewStyle } from 'react-native'
import { indexesForValue } from '@dms/helper'

import { programColorsForTimes } from 'zens-store'

import ScheduleSelection from './selection'
import Thumb from './thumb'
import Times from './times'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'stretch',
    justifyContent: 'center',
  },
  track: {
    opacity: 0,
  },
  thumb: {
    marginLeft: -3,
  },
  thumbDisabled: {
    backgroundColor: Color.metalGrey,
  },
  selection: {
    ...StyleSheet.absoluteFillObject,
    paddingLeft: 6,
    paddingVertical: 6,
  },
  slider: {
    height: 40,
    paddingLeft: 3,
  },
})

interface Data {
  startAt: string
  endAt: string
}

interface Props extends ViewProps {
  backgroundColor?: string
  disabled?: boolean
  value?: Data
  onChange?: (value: Data) => void
}

export interface ScheduleSliderRef {
  update: (newValue: [number, number]) => void
  select: (newValue: [number, number]) => void
}

const ScheduleSlider = forwardRef<ScheduleSliderRef, Props>((props, ref) => {
  const { value, onChange, backgroundColor, disabled, style, ...otherProps } = props
  const [indexes, setIndexes] = useState<[number, number]>(indexesForValue(value?.startAt, value?.endAt))

  const select = (newValue: number[]) => {
    setIndexes(newValue as [number, number])
  }

  const update = (newValue: number[]) => {
    const [startIndex, endIndex] = newValue
    const startAt = `${`${Math.floor(startIndex / 2) % 24}`.padStart(2, '0')}:${startIndex % 2 === 0 ? '00' : '30'}`
    const endAt = `${`${Math.floor(endIndex / 2) % 24}`.padStart(2, '0')}:${endIndex % 2 === 0 ? '00' : '30'}`
    onChange?.({
      startAt,
      endAt,
    })
  }

  useImperativeHandle<any, any>(ref, () => ({
    update: (newValue: [number, number]) => {
      update(newValue)
    },
    select: (newValue: [number, number]) => {
      select(newValue)
    },
  }))

  const containerStyle = [styles.container, { cursor: 'pointer' } as ViewStyle, style]
  const thumbStyle = [styles.thumb, disabled && styles.thumbDisabled]
  return (
    <View style={containerStyle} {...otherProps}>
      <View style={styles.slider} testID="ScheduleSlider.Container">
        <ScheduleSelection
          style={styles.selection}
          colors={programColorsForTimes(
            `${`${Math.floor(indexes[0] / 2) % 24}`.padStart(2, '0')}:${indexes[0] % 2 === 0 ? '00' : '30'}`,
            `${`${Math.floor(indexes[1] / 2) % 24}`.padStart(2, '0')}:${indexes[1] % 2 === 0 ? '00' : '30'}`,
            backgroundColor || '',
            disabled,
          )}
        />
        <Slider
          minimumValue={0}
          maximumValue={48}
          step={1}
          value={indexes}
          trackStyle={styles.track}
          renderThumbComponent={() => <Thumb style={thumbStyle} />}
          onValueChange={select}
          onSlidingComplete={update}
          disabled={disabled}
        />
      </View>
      <Times />
    </View>
  )
})

export default ScheduleSlider
